import { useOffersContextSafe } from '@/components/config-editor/providers/offers-provider';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import type { OfferItem } from '@/lib/api/offers';
import { useOffersSearch, useOffer } from '@/lib/api/offers';
import { formatMoney } from "@/lib/money";
import { cn } from "@/lib/utils";
import { ImageOff, Package, Search, X } from 'lucide-react';
import { memo, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface OfferSelectorProps {
  value: string;
  onChange: (value: string) => void;
  storeId?: string; // Store ID for server-side search
  // Dependency injection - optional offers data
  offers?: OfferItem[];
  loading?: boolean;
}

/**
 * Memoized offer card component
 */
const OfferCard = memo(
  ({
    offer,
    isSelected,
    getOfferTitle,
    getOfferSummary,
    calculateOfferTotal,
    formatExpiryDate,
    getVariantPriceInfo,
  }: {
    offer: any; // Accept any to handle optional promotions from backend
    isSelected: boolean;
    getOfferTitle: (offer: any) => string;
    getOfferSummary: (offer: any) => { itemCount: number };
    calculateOfferTotal: (offer: any) => { amount: number; currency: string } | null;
    formatExpiryDate: (date: string | Date | null) => string | null;
    getVariantPriceInfo: (lineItem: any) => { amount: number; currency: string } | null;
  }) => {
    const title = getOfferTitle(offer);
    const summary = getOfferSummary(offer);
    const totalPrice = calculateOfferTotal(offer);
    const expiryDate = formatExpiryDate(offer.expiryDate);
    const hasPromotions = offer.promotions && Array.isArray(offer.promotions) && offer.promotions.length > 0;
    const lineItems = offer.offerLineItems || [];

    return (
      <label
        className={cn(
          'block cursor-pointer rounded-lg border transition-all',
          'hover:border-primary/50 hover:bg-accent',
          isSelected && 'border-primary bg-accent ring-primary/20 ring-1',
        )}
      >
        <div className="p-2.5">
          {/* Header with radio button */}
          <div className="mb-2.5 flex items-start gap-2.5">
            <RadioGroupItem value={offer.id} className="shrink-0" />

            <div className="min-w-0 flex-1">
              <div className="mb-1 flex items-center justify-between gap-2">
                <h2 className="truncate text-sm font-medium leading-tight">{title}</h2>
                {totalPrice && (
                  <span className="text-primary shrink-0 text-sm font-semibold">
                    {formatMoney(totalPrice.amount, totalPrice.currency)}
                  </span>
                )}
              </div>

              <div className="text-muted-foreground space-y-0.5 text-xs">
                <p>
                  <span className="font-medium">Type:</span> {offer.type}
                  {summary.itemCount > 0 && (
                    <span className="ml-2">
                      • {summary.itemCount} {summary.itemCount === 1 ? 'item' : 'items'}
                    </span>
                  )}
                </p>

                {hasPromotions && (
                  <p>
                    <span className="font-medium">Promotions:</span> {offer.promotions?.length || 0} applied
                  </p>
                )}

                {expiryDate && (
                  <p>
                    <span className="font-medium">Expires:</span> {expiryDate}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Variants list */}
          {lineItems.length > 0 && (
            <div className="space-y-1.5 border-t pt-2.5">
              {lineItems.map((lineItem: any) => {
                const variant = lineItem.price?.variant;
                const product = variant?.product;
                const priceInfo = getVariantPriceInfo(lineItem);

                if (!variant) return null;

                return (
                  <div key={lineItem.id} className="bg-background flex items-center gap-2 rounded border p-2">
                    {variant.imageUrl ? (
                      <img
                        src={variant.imageUrl}
                        alt={variant.name}
                        className="h-10 w-10 shrink-0 rounded object-cover"
                      />
                    ) : (
                      <div className="bg-muted flex h-10 w-10 shrink-0 items-center justify-center rounded">
                        <ImageOff className="text-muted-foreground h-5 w-5" />
                      </div>
                    )}

                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-xs font-medium leading-tight">{variant.name}</h3>

                      {product && product.name && (
                        <p className="text-muted-foreground truncate text-xs">{product.name}</p>
                      )}
                    </div>

                    <div className="shrink-0 text-right">
                      {priceInfo && (
                        <p className="text-primary text-xs font-semibold">
                          {formatMoney(priceInfo.amount, priceInfo.currency)}
                        </p>
                      )}

                      {lineItem.quantity && lineItem.quantity > 1 && (
                        <p className="text-muted-foreground text-xs">×{lineItem.quantity}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </label>
    );
  },
);

OfferCard.displayName = 'OfferCard';

function OfferSelector({
  value,
  onChange,
  storeId,
  offers: injectedOffers,
  loading: injectedLoading,
}: OfferSelectorProps) {
  const instanceId = useId();
  const { t } = useTranslation('storefront');
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounced search update
  const updateDebouncedSearch = useDebouncedCallback((query: string) => {
    setDebouncedSearch(query);
  }, 300);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    updateDebouncedSearch(query);
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setDebouncedSearch('');
  };

  // Get offers from context or injected props
  const contextData = useOffersContextSafe();
  const contextOffers = injectedOffers !== undefined ? injectedOffers : contextData?.offers || [];
  const contextLoading = injectedLoading !== undefined ? injectedLoading : contextData?.loading || false;

  // Fetch selected offer separately (for initialization when it's not in the recent list)
  const { data: selectedOfferData, isLoading: isLoadingSelected } = useOffer(
    value || '',
    !!value && !!storeId && !debouncedSearch,
  );

  // Load all offers on mount when storeId is provided
  const { data: allOffersData, isLoading: isLoadingAll } = useOffersSearch(
    {
      storeId: storeId || '',
      search: '',
    },
    !!storeId && !debouncedSearch, // Only load when not searching
  );

  // Use server-side search when there's a search query and storeId
  const shouldSearch = !!storeId && !!debouncedSearch && debouncedSearch.trim().length > 0;

  const { data: searchResults, isLoading: isSearching } = useOffersSearch(
    {
      storeId: storeId || '',
      search: debouncedSearch,
    },
    shouldSearch,
  );

  // Determine which offers to display
  let allOffers: any[] = contextOffers;
  let loading = contextLoading;
  const DISPLAY_LIMIT = 100; // Client-side limit for performance

  if (storeId) {
    if (shouldSearch) {
      // User is searching
      allOffers = searchResults?.offers || [];
      loading = isSearching;
    } else {
      // Show all offers
      allOffers = allOffersData?.offers || [];
      loading = isLoadingAll || isLoadingSelected;
    }
  }

  // Client-side filtering and limiting with selected offer injection
  const offers = useMemo(() => {
    // If there's a selected value, inject it at the top if not already in the list
    if (value && selectedOfferData?.offer) {
      const isAlreadyInList = allOffers.some((o) => o.id === value);

      if (!isAlreadyInList) {
        // Selected offer is NOT in the list (e.g., offer #345 out of 500)
        // Inject it at the top, then show others up to limit
        const others = allOffers.slice(0, DISPLAY_LIMIT - 1);
        return [selectedOfferData.offer, ...others];
      } else {
        // Selected offer IS in the list, move it to top
        const selectedOffer = allOffers.find((o) => o.id === value);
        const others = allOffers.filter((o) => o.id !== value).slice(0, DISPLAY_LIMIT - 1);
        return [selectedOffer, ...others];
      }
    }

    // No selected value, just show first N offers
    return allOffers.slice(0, DISPLAY_LIMIT);
  }, [allOffers, value, selectedOfferData]);

  const hasMoreResults = allOffers.length > DISPLAY_LIMIT;

  // Memoized helper functions
  const getOfferTitle = useMemo(
    () =>
      (offer: OfferItem): string => {
        if (offer.titleTrans?.en) return offer.titleTrans.en;
        if (offer.titleTrans) {
          const keys = Object.keys(offer.titleTrans);
          if (keys.length > 0) return offer.titleTrans[keys[0]];
        }
        return `Offer ${offer.type}`;
      },
    [],
  );

  const getOfferSummary = useMemo(
    () =>
      (offer: OfferItem): { itemCount: number } => {
        const itemCount = offer.offerLineItems?.length || 0;
        return { itemCount };
      },
    [],
  );

  const formatExpiryDate = useMemo(
    () =>
      (date: string | Date | null): string | null => {
        if (!date) return null;
        try {
          const dateObj = typeof date === 'string' ? new Date(date) : date;
          return dateObj.toLocaleDateString();
        } catch {
          return null;
        }
      },
    [],
  );

  const getVariantPriceInfo = useMemo(
    () =>
      (lineItem: OfferItem['offerLineItems'][number]): { amount: number; currency: string } | null => {
        if (lineItem.price?.currencyOptions) {
          const currencies = Object.keys(lineItem.price.currencyOptions);
          if (currencies.length > 0) {
            const currency = currencies[0];
            const currencyData = lineItem.price.currencyOptions[currency];
            if (currencyData && typeof currencyData.amount === 'number') {
              return { amount: currencyData.amount, currency: currency.toUpperCase() };
            }
          }
        }
        return null;
      },
    [],
  );

  const calculateOfferTotal = useMemo(
    () =>
      (offer: OfferItem): { amount: number; currency: string } | null => {
        const lineItems = offer.offerLineItems || [];
        if (lineItems.length === 0) return null;

        let total = 0;
        let currency = '';

        for (const lineItem of lineItems) {
          const priceInfo = getVariantPriceInfo(lineItem);
          if (priceInfo) {
            total += priceInfo.amount * (lineItem.quantity || 1);
            if (!currency) {
              currency = priceInfo.currency;
            }
          }
        }

        return total > 0 ? { amount: total, currency } : null;
      },
    [getVariantPriceInfo],
  );

  // Only show skeletons on INITIAL load (no data yet)
  // This prevents the "accordion effect" when refreshing with existing data
  if (loading && offers.length === 0) {
    return (
      <div className="space-y-2">
        <div className="animate-pulse">
          <div className="mb-2 h-9 rounded bg-gray-200" />
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse rounded-md border p-3">
            <div className="flex items-start gap-3">
              <div className="h-4 w-4 rounded bg-gray-200" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-48 rounded bg-gray-200" />
                <div className="h-3 w-64 rounded bg-gray-200" />
                <div className="h-3 w-32 rounded bg-gray-200" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Search Input */}
      <div className="relative">
        <Search className="text-muted-foreground pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2" />
        <Input
          type="text"
          placeholder={t('configEditor.formElements.searchOffers')}
          value={searchQuery}
          onChange={handleSearchChange}
          className="h-9 pl-8 pr-8 text-sm"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={clearSearch}
            className="text-muted-foreground hover:text-foreground absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Results count */}
      {debouncedSearch && offers.length > 0 && (
        <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
          <Package className="h-3.5 w-3.5" />
          <span>
            {offers.length} {offers.length === 1 ? 'offer' : 'offers'}
          </span>
        </div>
      )}

      {/* More results available notice */}
      {!debouncedSearch && hasMoreResults && (
        <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 dark:border-blue-900 dark:bg-blue-950">
          <p className="text-xs text-blue-700 dark:text-blue-300">
            <strong>
              Showing {DISPLAY_LIMIT} of {allOffers.length} offers.
            </strong>{' '}
            Use search to find specific offers.
          </p>
        </div>
      )}

      {/* Offers List */}
      <RadioGroup name={`offer-selector-${instanceId}`} value={value} onValueChange={onChange}>
        <div className="h-[400px] overflow-y-auto p-1.5">
          <div className="space-y-3">
            {!offers || offers.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-8">
                <Package className="text-muted-foreground mb-2 h-8 w-8" />
                <p className="text-muted-foreground text-sm">
                  {debouncedSearch ? `No offers found matching "${debouncedSearch}"` : 'No offers available'}
                </p>
              </div>
            ) : (
              offers.map((offer) => (
                <OfferCard
                  key={offer.id}
                  offer={offer}
                  isSelected={value === offer.id}
                  getOfferTitle={getOfferTitle}
                  getOfferSummary={getOfferSummary}
                  calculateOfferTotal={calculateOfferTotal}
                  formatExpiryDate={formatExpiryDate}
                  getVariantPriceInfo={getVariantPriceInfo}
                />
              ))
            )}
          </div>
        </div>
      </RadioGroup>
    </div>
  );
}

export default OfferSelector;
